import React, { AriaRole } from 'react';
import type { AccessibilityProps } from 'react-native';
import { t } from '@lingui/macro';

import { CLASS_NAMES } from '@rover/constants/src/platform/classNames.constants';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import {
  AlertSeverity,
  getBackgroundColorForSeverity,
  getBorderColorForSeverity,
} from './Alert.common';
import type { Props as CommonProps } from './Alert.types';

export type Props = CommonProps &
  Omit<AccessibilityProps, 'role'> & {
    role?: AriaRole;
    className?: string;
    innerRef?: React.MutableRefObject<HTMLDivElement | null>;
    dismissIconTopRight?: boolean; // if true, dismiss icon will be placed on top right corner vs default center right
  };

const DismissIcon = (): JSX.Element => {
  const theme = useTheme();
  return (
    <Close
      style={{
        cursor: 'pointer',
        fill: theme.textColor.tertiary.toString(),
        width: theme.space['4x'],
        height: theme.space['4x'],
      }}
    />
  );
};

const DismissButton = ({ onDismiss }: { onDismiss?: () => void }): JSX.Element | null => {
  if (!onDismiss) return null;

  return (
    <Button
      width="4x"
      variant="flat"
      sx={{ ':hover': { background: 'none' } }}
      aria-label={t`Close Alert`}
      onClick={onDismiss}
    >
      <DismissIcon />
    </Button>
  );
};

const Alert = ({
  children,
  onDismiss,
  severity = AlertSeverity.INFO,
  className = '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  dismissIconTopRight = false,
  ...other
}: Props): JSX.Element | null => {
  const theme = useTheme();
  if (!children) return null;
  return (
    <Box
      display="flex"
      padding="4x"
      mb="6x"
      borderWidth="0x !important"
      borderStyle="solid !important"
      borderRadius="4x"
      sx={{
        backgroundColor: getBackgroundColorForSeverity(severity, theme),
        borderColor: getBorderColorForSeverity(severity, theme),
      }}
      className={`${CLASS_NAMES.ALERT_CLASSNAME} ${className}`}
      {...other}
    >
      <Flex flex="1" id="icon-wrapper">
        {children}
      </Flex>
      <Flex flexDirection={dismissIconTopRight ? 'column' : 'row'}>
        <DismissButton onDismiss={onDismiss} />
      </Flex>
    </Box>
  );
};

// attach AlertSeverity to default export for convenience
Alert.Severity = AlertSeverity;

export { AlertSeverity };

export default Alert;
